"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, ShieldCheck, Sparkles, CircleCheck as CheckCircle2, PhoneCall, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const steps = [{
  stepNumber: "01",
  title: "Inspection & testing",
  icon: Search,
  phase: "Phase 1: Discovery",
  description: "We trace hidden moisture sources behind surfaces, map affected square footage with infrared sensors, and document spore distribution before work begins.",
  details: "Thermal moisture mapping, air quality baseline, containment plan."
}, {
  stepNumber: "02",
  title: "Containment",
  icon: ShieldCheck,
  phase: "Phase 2: Isolation",
  description: "We erect sealed 6-mil poly barriers and install continuous HEPA negative air machines to isolate the work zone and prevent cross-contamination to clean rooms.",
  details: "Negative pressure airlocks, HEPA exhaust venting, HVAC protection."
}, {
  stepNumber: "03",
  title: "Removal & treatment",
  icon: Sparkles,
  phase: "Phase 3: Mitigation",
  description: "Our technicians bag and remove contaminated porous materials like rotted drywall, HEPA-vacuum structural framing, and apply EPA-registered antimicrobials.",
  details: "Controlled demolition, direct HEPA cleaning, antimicrobial wash."
}, {
  stepNumber: "04",
  title: "Prevention & clearance",
  icon: CheckCircle2,
  phase: "Phase 4: Verification",
  description: "We treat remaining structural lumber with clear fungicidal sealant and arrange independent clearance air testing to confirm the space is certified clean.",
  details: "Fungicidal barrier seal, independent third-party air test, warranty."
}];
export default function Process() {
  const prefersReducedMotion = useReducedMotion();
  return <section id="our-process" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary/10 border border-primary/20 text-primary text-xs font-bold tracking-wider uppercase mb-4">
            <span className="h-2 w-2 rounded-full bg-primary" />
            Standard operating procedure
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-4">
            Our remediation protocol
          </h2>

          <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-4">
            How our certified local crews contain, eliminate, and verify mold in your home or facility.
          </p>

          <p className="text-base text-muted-foreground leading-relaxed">
            Mold remediation requires strict environmental engineering, not just surface wiping. We follow IICRC S520 guidelines to ensure active colonies are completely isolated, physically extracted, and cleared through objective air quality tests before reconstruction begins.
          </p>
        </div>

        {/* 4-Step Process Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => {
          const Icon = step.icon;
          return <motion.div key={step.stepNumber} data-index={i} initial={prefersReducedMotion ? false : {
            opacity: 0,
            y: 16
          }} whileInView={prefersReducedMotion ? undefined : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.35,
            delay: i * 0.08,
            ease: "easeOut"
          }} className="h-full flex">
                <Card className="relative flex flex-col justify-between w-full rounded-lg bg-card border border-border transition-[border-color,box-shadow] duration-200 hover:border-primary/60 hover:shadow-md pt-6">
                  <CardContent className="flex flex-col h-full p-6">
                    {/* Top Index & Icon Row */}
                    <div className="flex items-center justify-between gap-4 mb-6">
                      <div className="flex items-center justify-center size-12 rounded-md bg-primary/10 text-primary border border-primary/20">
                        <Icon className="size-6 text-primary" />
                      </div>
                      <span className="font-mono text-2xl font-bold text-muted-foreground/50">
                        {step.stepNumber}
                      </span>
                    </div>

                    {/* Step Title & Content */}
                    <div className="mb-4">
                      <p className="text-xs font-semibold uppercase tracking-wider text-primary mb-1">
                        {step.phase}
                      </p>
                      <h3 className="text-xl font-bold text-foreground mb-3">
                        {step.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {step.description}
                      </p>
                    </div>

                    {/* Footer Specifications */}
                    <div className="mt-auto pt-4 border-t border-border">
                      <p className="text-xs font-medium text-foreground">
                        Protocol scope:
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {step.details}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Operational Dispatch Banner */}
        <div className="mt-12 md:mt-16 p-6 sm:p-8 rounded-lg bg-muted border border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h4 className="text-lg sm:text-xl font-bold text-foreground mb-1">
              Need immediate containment or moisture diagnostics?
            </h4>
            <p className="text-sm sm:text-base text-muted-foreground">
              Direct dispatch across Maryland, Washington DC, and Northern Virginia. Talk directly with our restoration supervisors with no call center delays.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <a href="tel:855-435-6631">
                <PhoneCall className="size-4 mr-2" />
                Call 855-435-6631
              </a>
            </Button>

            <Button asChild variant="outline" size="lg" className="border-border hover:bg-background">
              <Link to="/ContactRequestServicePage">
                <span>Request on-site evaluation</span>
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}